import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {useSelector} from "react-redux";
import ValidationContext from '@/Contexts/ValidationContext';
import i18n from 'i18next';
import {
    formatCurrency,
    formatCurrencyWithSymbol,
    formatPhone,
    isNullOrWhiteSpace,
    parseNumber
} from '@/Utilities/Util';
import Select from 'react-select';
import {isTouchDevice} from '@/constants'
import {hasEllipsis} from "@/Utilities/Ui";
import CountrySelect from "react-bootstrap-country-select";
import '@/../sass/components/CountrySelect.scss'
import '@/../sass/components/FormSelect.scss';
import "@/../sass/components/FormRadio.scss";
import {FieldError} from "./FieldComponents";

export function FormInput(p) {
    const getStyle = getStyles();
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));
    let type = "text";
    if (p.format === "digits")
        type = "number";
    if (p.format === "password") {
        type = passwordShown ? "text" : "password";
    }
    if (p.format === "email")
        type = "email";

    const label = p.helpTextTop ? <Fragment><span>{p.label}</ span><span style={getStyle('fieldLabels')} className="help-text-top">{p.helpTextTop}</span></Fragment> : p.label;

    const [passwordShown, setPasswordShown] = useState(false);
    const [eyeIcon, setEyeIcon] = useState('fa-eye-slash');
    const togglePasswordVisibility = () => {
        if (passwordShown) {
            setEyeIcon('fa-eye-slash');
            setPasswordShown(false);
            type = "password";
        } else {
            setEyeIcon('fa-eye');
            setPasswordShown(true);
            type = "text";
        }
    };

    return (
        <Validation className={"form-group " + p.className + (p.value ? " field-has-value" : "") + (p.value || determineLabelPlacement(p.mode, p.label, true) ? " animate-on-load" : "")} valid={!error} validationMessage={error}>
            {label !== "\u00a0" && <label className={determineLabelPlacement(p.mode, p.label) + " control-label " + (p.labelClass ? p.labelClass : "") + (p.disabled ? " label-disabled" : "")} style={getStyle('fieldLabels')}>{label}</label>}
            <input className="form-control"
                   autoFocus={p.isFocused}
                   disabled={p.disabled || false}
                   name={p.name}
                   value={p.value || ""}
                   onChange={(e) => onInputChange(e, p)}
                   onFocus={(e) => { p.onFocus && p.onFocus(); onFocusAnimation(e) }}
                   onBlur={(e) => {onInputBlur(e, p); onBlurAnimation(e)}}
                   onKeyUp={p.onKeyUp}
                   type={p.format !== 'password' ? type : (passwordShown ? 'text' : 'password')}
                   style={getStyle('inputs')}
                   maxLength={p.maxLength}
                   placeholder={p.placeholder}
                   /* TODO: This data-qa should be deleted after migrating all pages */
                   data-qa={p.dataQa}
                   data-testid={p.dataTestid}
                   id={p.id}
            />
            {p.visibilityIcon && <i onClick={togglePasswordVisibility} className={"fal fa-light " + eyeIcon} id="password-visibility"></i> }
            {p.mode === "completed-form" && <span className="completed-form-input form-control" style={getStyle('inputs')}>{p.format === "currency" ? formatCurrency(p.value) : p.value}</span>}
            {p.helpText && <span style={getStyle('helpText')} data-testid="controls_input_span-help_block" className="help-block">{p.helpText}</span>}
        </Validation>
    );
}

function isPhone(value) {
    if (! value) {
        return false;
    }

    const phoneRegEx = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
    return value.length >= 10 && phoneRegEx.test(value);
}

function onBlurPhoneOrEmail(e, p) {
    if (isPhone(p.value)) {
        onPhoneChanged(p, e, true);
        onBlurAnimation(e);
    } else {
        onInputBlur(e, p);
        onBlurAnimation(e);
    }
}

function onPhoneOrEmail(e, p) {
    if (isPhone(p.value))
        onPhoneChanged(p, e);
    else
        onInputChange(e, p);
}

export function PhoneOrEmailInput(p) {
    const getStyle = getStyles();
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));
    let inputValue = p.value || "";
    let type = "text";
    if (isPhone(p.value)) {
        type = "tel";
        inputValue = formatPhone(inputValue);
    }

    const label = p.helpTextTop ? <Fragment><span>{p.label}</ span><span style={getStyle('fieldLabels')} className="help-text-top">{p.helpTextTop}</span></Fragment> : p.label;

    return (
        <Validation className={"form-group " + p.className + (p.value ? " field-has-value" : "") + (p.value || determineLabelPlacement(p.mode, p.label, true) ? " animate-on-load" : "")} valid={!error} validationMessage={error}>
            {label !== "\u00a0" && <label className={determineLabelPlacement(p.mode, p.label) + " control-label " + (p.labelClass ? p.labelClass : "") + (p.disabled ? " label-disabled" : "")} style={getStyle('fieldLabels')}>{label}</label>}
            <input className="form-control" autoFocus={p.isFocused} disabled={p.disabled || false} name={p.name} value={inputValue} onChange={(e) => onPhoneOrEmail(e, p)} onFocus={(e) => { p.onFocus && p.onFocus(); onFocusAnimation(e) }} onBlur={(e) => {onBlurPhoneOrEmail(e, p)}} onKeyUp={p.onKeyUp} onKeyDown={p.onKeyDown} type={type} style={getStyle('inputs')} maxLength={p.maxLength} placeholder={p.placeholder} /* TODO: This data-qa should be deleted after migrating all pages */ data-qa={p.dataQa} data-testid={p.dataTestid} />
            {p.mode === "completed-form" && <span className="completed-form-input form-control" style={getStyle('inputs')}>{p.value}</span>}
            {p.helpText && <span style={getStyle('helpText')} data-testid="controls_input_span-help_block" className="help-block">{p.helpText}</span>}
        </Validation>
    );
}

export function FormInputCountrySelector(p) {
    const getStyle = getStyles();
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));

    const [chevronDirection, setChevronDirection] = useState("down");

    const label = p.helpTextTop ? <Fragment><span>{p.label}</ span><span style={getStyle('fieldLabels')} className="help-text-top">{p.helpTextTop}</span></Fragment> : p.label;

    return (
        <Validation className={"form-group " + p.className + (p.value ? " field-has-value" : "") + (p.value || determineLabelPlacement(p.mode, p.label, true) ? " animate-on-load" : "")} valid={!error} validationMessage={error} dataTestid={p.dataTestid}>
            {label !== "\u00a0" && <label className={determineLabelPlacement(p.mode, p.label) + " control-label " + (p.labelClass ? p.labelClass : "") + (p.disabled ? " label-disabled" : "")} style={getStyle('fieldLabels')}>{label}</label>}
            <div
                className="country-select-container"
                onFocus={(e) => { p.onFocus && p.onFocus(); onFocusAnimation(e); setChevronDirection("up") }}
                onBlur={(e) => {onBlurAnimation(e); setChevronDirection("down")}}
                onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault(); }}
            >
                <CountrySelect
                    name={p.name}
                    value={p.value || ""}
                    valueAs="id"
                    matchAbbreviations={true}
                    matchNameFromStart={false}
                    onChange={(e) => p.onChange(e)}
                    data-qa={p.dataQa}
                    placeholder=""
                    listMaxHeight={p.listMaxHeight || 200}
                ></CountrySelect>
                <i className={chevronDirection === "up" ? "fa fa-chevron-up" : "fa fa-chevron-down"} />
            </div>
            {p.mode === "completed-form" && <span className="completed-form-input form-control" style={getStyle('inputs')}>{p.format === "currency" ? formatCurrency(p.value) : p.value}</span>}
            {p.helpText && <span style={getStyle('helpText')} data-testid="controls_input_span-help_block" className="help-block">{p.helpText}</span>}
        </Validation>
    );
}

export function FormIconInput(p) {
    const getStyle = getStyles();
    const [state, refresh] = useState(false);
    const ref = useRef({});
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));
    let type = "text";
    if (p.format === "digits")
        type = "number";
    if (p.format === "password")
        type = "password";

    const inFocus = ref.current === document.activeElement;

    return (
        <Validation className={"form-group " + p.className + (p.value ? " animate-on-load field-has-value" : "")} valid={!error} validationMessage={error}>
            {p.label &&
                <label className={determineLabelPlacement(p.mode, p.label) + " control-label field-label-addon" + (p.disabled ? " label-disabled" : "")} style={getStyle('fieldLabels')}>{p.label}</label>
            }
            <div className="input-group">
                <div className={"input-group-addon" + (inFocus ? ' input-focus' : "")}>{p.icon}</div>
                <input
                    className="form-control"
                    disabled={p.disabled || false}
                    name={p.name}
                    value={p.value || ""}
                    onFocus={(e) => { refresh(); onFocusAnimation(e) }}
                    onChange={(e) => onInputChange(e, p)}
                    onBlur={(e) => { refresh({}); onInputChange(e, p); onBlurAnimation(e); }}
                    type={type}
                    //style={getStyle('inputs')} component only in builder
                    maxLength={p.maxLength}
                    placeholder={p.placeholder}
                    data-qa={p.dataQa}
                    data-keyboard={p.format === "digits" ? "numeric" : ""} // for inside the kiosk app
                    ref={ref} />
            </div>
            {p.helpText && <span style={getStyle('helpText')} data-testid="controls_input_span-help_block" className="help-block">{p.helpText}</span>}
        </Validation>
    );
}

export function CurrencyInput(props) {
    const getStyle = getStyles();
    const [inFocus, setInFocus] = useState(false);

    function onFocus(e) {
        setInFocus(true);
        if (props.onFocus)
            props.onFocus(e);
    }

    function onBlur(e) {
        setInFocus(false);
        if (props.onBlur)
            props.onBlur(e);
        onInputChange(e, props);
    }

    if (props.mode === "completed-form") {
        return <span className="completed-form-input form-control" style={getStyle('inputs')}>{props.value}</span>
    }

    let value = parseNumber(props.value || 0);
    value = value ? value.toFixed(2) : "";

    return (
        <div className="input-group">
            <div className={"input-group-addon" + (inFocus ? " input-focus" : "") + (props.disabled ? " input-disabled" : "")} style={getStyle('inputsAddOn')}>$</div>
            <input
                {...props}
                type="text"
                name={undefined}
                inputMode="decimal"
                onFocus={(e) => { onFocus(e); onFocusAnimation(e) }}
                onBlur={(e) => { onBlur(e); onBlurAnimation(e) }}
                onChange={e => onInputChange(e, props)}
                data-keyboard="numeric" // for inside the kiosk app
            />
            <input type="hidden" name={props.name} value={value} />
        </div>
    );
}

export function FormCurrency({ className, name, value, label, onChange, mode, error, required, dataQa }) {
    const getStyle = getStyles();
    const validation = useContext(ValidationContext);
    value = (mode === "completed-form" ? formatCurrencyWithSymbol(value) : value);
    error = error || (validation && validation.show && validation.getError(name));

    function focus() {
        if (value === "0" || value === "0.00")
            onChange(name, "");
    }

    function change(name, value) {
        onChange(name, value);
    }

    function blur(e) {
        onChange(name, formatCurrency(e.target.value || 0));
    }

    return (
        <div className={"form-group " + (className || "") + (value ? " animate-on-load field-has-value" : "") + (error ? " ng-invalid has-error" : "")}>
            <label className={"control-label field-label-addon " + (mode !== "completed-form" ? "field-label " : "")} style={getStyle('fieldLabels')}>{label}</label>
            <div>
                <CurrencyInput
                    name={name}
                    value={value}
                    onFocus={focus}
                    onChange={change}
                    onBlur={blur}
                    format="currency"
                    className={"form-control" + (error ? " ng-invalid" : "") + (required ? " requiresamount" : "")}
                    style={getStyle('inputs')}
                    mode={mode}
                    data-qa={dataQa}
                />
                <FieldError error={error} getStyle={getStyle} />
            </div>
        </div>
    );
}

export function FormCheckbox(p) {
    const getStyle = getStyles();
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));

    return (
        <Validation className={"form-group " + p.className} valid={!error} validationMessage={error}>
            <div className="checkbox">
                <label className="control-label" style={getStyle('inputsListLabel')}>
                    <input name={p.name} checked={p.value} onChange={(e) => onCheckChange(e, p)} type="checkbox" disabled={p.disabled || false} style={getStyle('inputs')} data-qa={p.dataQa} /* TODO: This data-qa should be deleted after migrating all pages */ data-testid={p.dataTestid} />
                    <span data-testid={p.dataTestid+"_label"}>{p.label}</span>
                    <span className="helper" />{/* helper span is for clover */}
                </label>
            </div>
        </Validation>
    );
}

export function FormRadio(p) {
    const getStyle = getStyles();
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));

    return (
        <Validation className={"form-radio form-group " + p.className} valid={!error} validationMessage={error}>
            <label className="control-label" style={getStyle('fieldLabels')}>{p.label}</label>
            <div className="radio" data-qa={p.dataQa} /* TODO: This data-qa should be deleted after migrating all pages */ data-testid={p.dataTestid}>
                {p.options.map((o, index) =>
                    <label className={`control-label${(getKey(o) === p.value)? " radio-checked" : ""}`} style={getStyle('fieldLabels')} key={getKey(o)}>
                        <input name={p.name} value={getKey(o)} checked={getKey(o) == p.value} onChange={(e) => onRadioChange(e, p)} type="radio" disabled={p.disabled || false} style={getStyle('inputs')} data-testid={p.dataTestid+"_"+index} />
                        <span data-testid={p.dataTestid+"_span_"+index}>{getName(o)}</span>
                        <span className="helper" />
                    </label>
                )}
            </div>
        </Validation>
    );
}


export function FormSelect(p) {
    const getStyle = getStyles();
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));
    const options = !p.customFilter ? (p.options || []).map(o => ({ value: getKey(o) + "", label: getName(o), isDisabled: o.disabled })) : p.options;
    const selected = p.isMulti ? options.filter(o => p.value && p.value.indexOf(o.value) >= 0) : options.find(o => o.value == p.value) || null;

    const inputStyle = getStyle("inputs");

    return (
        <Validation className={"form-group " + p.className + (selected ? " animate-on-load field-has-value" : "")} valid={!error} validationMessage={error} dataQa={p.dataQa} dataTestid={p.dataTestid} /* TODO: This data-qa should be deleted after migrating all pages */>
            <label className={"field-label control-label select-label" + (p.labelClass ? " " + p.labelClass : "") + (p.disabled ? " label-disabled" : "")} style={getStyle('fieldLabels')}>{p.label}</label>
            <Select
                menuPlacement={p.menuPlacement ? p.menuPlacement : "auto"}
                maxMenuHeight={260}
                isSearchable={p.isSearchable !== false}
                className={"search-select" + (p.enableClear && selected ? " clear-visible" : "") + " record-payment" + (inputStyle && inputStyle.backgroundColor ? " custom-background-color" : "") + (inputStyle && inputStyle.textColor ? " custom-text-color" : "")}
                name={p.name}
                value={selected}
                onFocus={(e) => onFocusAnimation(e)}
                onBlur={(e) => onBlurDropdownAnimation(e, selected)}
                onChange={(item) => onSelectChange(item, p)}
                options={options}
                placeholder={p.placeholder || "Select..."}
                isClearable={p.enableClear}
                isDisabled={p.disabled || false}
                classNamePrefix="ss"
                filterOption={p.customFilter}
                isMulti={p.isMulti}
                closeMenuOnSelect={p.closeMenuOnSelect !== undefined ? p.closeMenuOnSelect : true}
            />
            {p.note &&
                <span data-testid="controls_input_span-help_block" className="help-block">{p.note}</span>
            }
        </Validation>
    );
}

export function FormTextarea(p) {
    const getStyle = getStyles();
    const v = useContext(ValidationContext);
    const error = p.error || (v && v.show && v.getError(p.name));
    let style = getStyle('inputs') || {};
    if (p.resize === false)
        style = { ...style, resize: "none" };

    return (
        <Validation className={"form-group " + p.className + (p.value ? " field-has-value" : "") + (p.value || determineLabelPlacement(p.mode, p.label, true) ? " animate-on-load" : "")} valid={!error} validationMessage={error}>
            <label className={determineLabelPlacement(p.mode, p.label) + " control-label" + (p.disabled ? " label-disabled" : "")} style={getStyle('fieldLabels')}>{p.label}</label>
            <textarea className={"form-control " + (p.controlClass || "")} rows={p.rows} name={p.name} value={p.value || ""} onChange={(e) => onTextareaChange(e, p)} onFocus={(e) => onFocusAnimation(e)} onBlur={(e) => onBlurAnimation(e)} disabled={p.disabled || false} style={style} data-qa={p.dataQa} /* TODO: This data-qa should be deleted after migrating all pages */ data-testid={p.dataTestid} />
        </Validation>
    );
}

export function PostalCodeLabel(props) {
    const country = useSelector(store => store.config.countryCode);
    const customPath = props ? props.t : null;

    let translation;
    if (country === "CA")
        translation = customPath ? customPath.replace('zip-code', 'postal-code') : "form.postal-code";
    else
        translation = customPath || "form.zip-code";
    return i18n.t(translation);
}

export function StateLabel(props) {
    const country= useSelector(store => store.config.countryCode);
    const customPath = props ? props.t : null;

    let translation;
    if (country === "CA")
        translation = customPath ? customPath.replace('state', 'province') : "form.province";
    else
        translation = customPath || "form.state";
    return i18n.t(translation);
}

export function PhoneInput(p) {
    const getStyle = getStyles();
    return (
        <Fragment>
            <label className="field-label control-label" style={getStyle('fieldLabels')}>{p.label}</label>
            <input {...p} onFocus={(e) => onFocusAnimation(e)} onChange={e => onPhoneChanged(p, e) } onBlur={e => { onPhoneChanged(p, e, true); onBlurAnimation(e) }} type="tel" data-keyboard="numeric" />
        </Fragment>
    );
}

export function FormTooltip({ show, tooltip, children, placement, className, trigger, onlyIfTruncatedRef }) {
    // OnlyIfTruncated
    const [needed, setNeeded] = useState(true);
    useEffect(() => {
        if (!onlyIfTruncatedRef || !onlyIfTruncatedRef.current)
            return;

        const truncated = hasEllipsis(onlyIfTruncatedRef.current);
        if (needed !== truncated)
            setNeeded(truncated);
    });
    if (!needed || (show !== undefined && !show))
        return children;

    return (
        <OverlayTrigger placement={placement || "top"} trigger={trigger} rootClose={true} overlay={<Tooltip className={"fms-tooltip " + (className || "")}>{tooltip}</Tooltip>}>
            {children}
        </OverlayTrigger>
    );
}

export function Validation(props) {
    /* TODO: This data-qa should be deleted after migrating all pages */
    const dataQa = props.dataQa ? { "data-qa": props.dataQa } : {};
    const dataTestid = props.dataTestid ? { "data-testid": props.dataTestid } : {};
    return (
        <div className={props.className + (!props.valid ? " has-error" : "")} {...dataQa} {...dataTestid}>
            {props.children}
            <ValidationMessage valid={props.valid} validationMessage={props.validationMessage} />
        </div>
    );
}

export function ValidationMessage(props) {
    if (props.valid)
        return null;

    const getStyle = getStyles();
    return <span style={getStyle('helpText')} className="help-block has-error error-msg" data-testid="controls_input_span-help_block"><i className="fas fa-exclamation-circle"></i> {props.validationMessage}</span>
}


//Input, IconInput, CurrencyInput, Phone Input
export function onBlurAnimation(event) {
    //Material Animation
    const container = event && event.target.closest(".form-group");
    if (container) {
        if (event.target.value == "")
            container.removeAttribute("animate");

        container.removeAttribute("inFocus");
    }
}

export function onFocusAnimation(event) {
    //Material Animation
    const container = event && event.target.closest(".form-group");
    if (container) {
        if (!container.hasAttribute("animate"))
            container.setAttribute("animate", "true");

        container.setAttribute("inFocus", "true");
    }
}

export function onBlurDropdownAnimation(event, selected) {
    //Material Animation
    const container = event && event.target.closest(".form-group");
    if (container) {
        if (!selected)
            container.removeAttribute("animate");

        container.removeAttribute("inFocus");
    }
}

const builderWidth = document.getElementById("unity").offsetWidth;
export function determineLabelPlacement(mode, label, formGroup, field) {

    function checkLabelWidth(mode, label, formGroup, smallLabelCount, largeLabelCount) {
        if (formGroup) {
            if (label && (label.length > smallLabelCount) && (label.length < largeLabelCount)) {
                return true;
            }
        } else {
            if (mode === "completed-form") {
                return "outside-label";
            }
            if (label) {
                if (label.length < largeLabelCount) {
                    return "field-label";
                } else if (label.props && (Object.keys(label.props).length === 0 && label.props.constructor === Object)) {
                    return "field-label";
                } else if (label.props && label.props.label && (label.props.label.length < largeLabelCount)) { //label.props.showRequired - postalcode
                    return "field-label";
                } else if (label.props && label.props.tooltip) { //processing fee
                    return "field-label";
                } else if (label.props && label.props.form) { //registration email field
                    return "field-label";
                }
            }
            return "outside-label";
        }
    }

    if (document.body.classList.contains("giving-kiosk-app")) {
        if (field && field.isPaymentField) { //giving kiosk has different width for payment fields
            if (field.templateName === "fund-dropdown")
                return checkLabelWidth(mode, label, formGroup, 50, 71);
            else
                return checkLabelWidth(mode, label, formGroup, 12, 19);
        } else { //giving kiosk has different width for non-payment fields
            return checkLabelWidth(mode, label, formGroup, 20, 25);
        }
    } else if (builderWidth >= 275 && builderWidth <= 425) { //mobile resolution
        return checkLabelWidth(mode, label, formGroup, 25, 35);
    } else {
        if (field && field.step) { //steps have different width form
            return checkLabelWidth(mode, label, formGroup, 15, 29);
        } else {
            return checkLabelWidth(mode, label, formGroup, 35, 45);
        }
    }
}

function getKey(o) {
    if (o.key !== undefined && o.key !== null)
        return o.key;
    if (o.id !== undefined && o.id !== null)
        return o.id;
    return getName(o);
}

function getName(o) {
    if (o.name !== undefined && o.name !== null)
        return o.name;
    return o;
}

function getStyles() {
    return getNoStyles;
    //const context = useContext(ThemeContext);
    //return (context && context.getStyle ? context.getStyle : getNoStyles);
}

function getNoStyles() {
    // Intentionally blank
}

function onInputChange(event, props) {
    let value = event.target.value;

    if (props.format === "currency") {
        const parts = value.split(".");
        if (parts.length > 2)
            value = parts[0] + '.' + parts[1];

        value = value.replace(/[^0-9.,]/g, '');

        if (event.type === "blur")
            value = formatCurrency(value);
    }
    else if (props.format === "digits") {
        value = value.replace(/[^0-9]/g, '');
        if (props.maxLength && value.length > props.maxLength)
            value = value.substr(0, props.maxLength);
    } else if (props.format === "number") {
        value = value.replace(/[^0-9-.]/g, '');
        if (value.lastIndexOf('-') > 0)
            value = value[0] + value.substr(1).replace(/[-]/g, '');
        if (value.indexOf('.') !== value.lastIndexOf('.')) {
            const split = value.split('.');
            value = split.shift() + "." + split.join("");
        }
    }

    if (value !== props.value && props.onChange)
        props.onChange(props.name, value, props.index);
}

function onInputBlur(event, props) {
    onInputChange(event, props);
    if (props.onBlur)
        props.onBlur(event);
}

function onDateChange(value, props) {
    if (props.onChange)
        props.onChange(props.name, value);
}

function onCheckChange(event, props) {
    if (props.onChange)
        props.onChange(props.name, event.target.checked);
}

function onRadioChange(event, props) {
    if (props.onChange)
        props.onChange(props.name, event.target.value);
}

function onSelectChange(item, props, validation) {
    let value = null;
    if (item) {
        if (props.isMulti)
            value = item.map(o => o.value);
        else
            value = item.value;
    }
    if (props.onChange)
        props.onChange(props.name, value);
}

function onTextareaChange(event, props) {
    if (event.target.value !== props.value && props.onChange)
        props.onChange(props.name, event.target.value);
}

function onPhoneChanged(props, ev, isBlur) {
    const target = ev.target;
    const value = formatPhone(target.value);
    const cursor = target.selectionEnd;

    // Maintain cursor position so not always at end - needs work
    /*if (!isBlur && cursor !== target.value.length) {
        const tNeighbors = target.value[cursor - 1] + target.value[cursor];
        const vNeighbors = value[cursor - 1] + value[cursor];

        let added = 0;
        const t = target.value.indexOf(tNeighbors);
        const v = value.indexOf(vNeighbors);

        if (v > t)
            added = added + (v - t)
        else if (t > v)
            added = added - (t - v)

        setTimeout(function () { target.selectionEnd = cursor + added; }, 10);
    }*/

    if (props.onChange)
        props.onChange(props.name, value);
}

export function stopCountrySelectFromSubmittingForm(e) {
    e.preventDefault();
    e.stopPropagation();
    // e.stopImmediatePropagation();
    console.log(e);
}

export function required(value) {
    return isNullOrWhiteSpace(value) ? i18n.t('form.validation.required') : null;
}

export function termsLabel () {
    return (
        <span>{i18n.t("signup.i-agree-to")} <a href="#">{i18n.t("signup.terms-and-conditions")}</a></span>
    );
}
